import React, { useState, useRef, useCallback } from "react";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import * as Icons from "react-feather";
import * as Router from "react-router-dom";
import { Office } from "model/office";
import * as paths from "routes/paths";
import { styles } from "./styles";
import { ProfileTab } from "./tab-profile";
import { WebsiteTab } from "./tab-website";
import { SettingsStickyFooter } from "component/shared/settings-sticky-footer";
import { UnsavedChangesPrompt } from "component/shared/unsaved-changes-prompt";
import { TabHandle } from "component/shared/profile-refresh/tab-handle";

interface Props extends OwnProps, WithStyles<typeof styles>, Router.RouteComponentProps {}

interface OwnProps {
	office: Office;
}

const TAB_LABELS = ["Profile", "Website"];

/**
 * Refreshed Office detail view (MAX-4065). Structurally the profile-refresh shell — back link,
 * tab strip, one shared sticky save bar driven by the active tab's TabHandle — without the left
 * summary panel, which the Figma replaces with the office name as the page title.
 */
const OfficeDetailRefreshComponent: React.FC<Props> = ({ office, classes = {}, history }) => {
	const [tab, setTab] = useState(0);
	const [saving, setSaving] = useState(false);
	// Re-render the footer when a tab reports its dirty state changed.
	const [, setDirtyTick] = useState(0);
	const tabRef = useRef<TabHandle>(null);
	// Stable identity so tab effects that depend on it don't re-fire every render (avoids a loop).
	const onDirtyChange = useCallback(() => setDirtyTick((t) => t + 1), []);

	if (!office) {
		return null;
	}

	const handleSave = async () => {
		if (!tabRef.current) {
			return;
		}
		setSaving(true);
		await tabRef.current.save();
		setSaving(false);
		onDirtyChange();
	};

	const handleCancel = () => {
		tabRef.current?.cancel();
		onDirtyChange();
	};

	const dirty = tabRef.current ? tabRef.current.isDirty() : false;

	// Deep-links to the Team page with this office preselected in the filter menu.
	const viewOfficeMembers = () =>
		history.push(`${paths.settingsAgents}?officeId=${office.id}`);

	return (
		<div className={classes.root}>
			<UnsavedChangesPrompt isDirty={() => (tabRef.current ? tabRef.current.isDirty() : false)} history={history} />
			<div className={classes.content}>
				<Mui.Link className={classes.backLink} onClick={() => history.push(paths.settingsOffices)}>
					<Icons.ArrowLeft size={16} />
					Back to Offices
				</Mui.Link>

				<Mui.Typography className={classes.title}>{office.name}</Mui.Typography>

				<div className={classes.tabRow}>
					<div className={classes.tabBar}>
						{TAB_LABELS.map((label, index) => (
							<button
								key={label}
								type="button"
								className={`${classes.tab} ${index === tab ? classes.tabActive : ""}`}
								onClick={() => setTab(index)}
							>
								<span className={classes.tabLabel}>{label}</span>
								<span className={`${classes.tabUnderline} ${index === tab ? classes.tabUnderlineActive : ""}`} />
							</button>
						))}
					</div>

					<Mui.Link className={classes.viewMembersLink} onClick={viewOfficeMembers}>
						View Office Members
						<Icons.ExternalLink size={14} />
					</Mui.Link>
				</div>

				{/* key forces a fresh tab instance (+ ref) on tab change */}
				{tab === 0 && <ProfileTab key="profile" ref={tabRef} office={office} classes={classes} onDirtyChange={onDirtyChange} />}
				{tab === 1 && <WebsiteTab key="website" ref={tabRef} office={office} classes={classes} onDirtyChange={onDirtyChange} />}
			</div>

			<SettingsStickyFooter dirty={dirty} saving={saving} onCancel={handleCancel} onSave={handleSave} />
		</div>
	);
};

export const OfficeDetailRefresh = withStyles(Router.withRouter(OfficeDetailRefreshComponent), styles);
